import { useState, useCallback, useTransition, type ChangeEvent, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { Alert, Button } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { Field } from '@/components/common/Field'
import { GoogleIcon, MicrosoftIcon } from '@/components/common/ProviderIcons'
import { authService } from '@/services/auth'
import { ACCOUNT_LOCKED_CODE, AuthRequestError, authErrorMessage } from '@/services/auth-errors'
import { fetchRetryingWhenBusy } from '@/services/busy-retry'
import { readSessionHomeUrl } from '@/services/session-home'
import { homeAfterAcceptance } from '@/services/invitation-landing'
import { config } from '@/utils/config'
import { hasEmailShape } from '@/utils/validation'
import {
  buildExternalOAuthLoginUrl,
  resolveAuthenticationContinuation,
  type ExternalOAuthProvider,
} from '@/utils/auth-continuation'

export const LoginPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isPending, startTransition] = useTransition()

  // The caller's continuation, when the sign-in link carries one. Without it
  // auth-service decides where the member lands: a Guest in the chat app,
  // every other role where it landed before (ADR-20260930 D7). Login UI never
  // derives an app address itself.
  const searchParams = new URLSearchParams(window.location.search)
  const returnTo = searchParams.get('return_to') || undefined

  // Use useCallback to memoize handlers and avoid recreation on every render
  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) setError('')
    if (emailError) setEmailError('')
  }, [error, emailError])

  const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (error) setError('')
  }, [error])

  const handleExternalLogin = (provider: ExternalOAuthProvider) => {
    try {
      const continuation = returnTo
        ? resolveAuthenticationContinuation(returnTo, config.authServiceUrl)
        : undefined
      const invitationToken = sessionStorage.getItem('invitation_token') || undefined
      window.location.href = buildExternalOAuthLoginUrl(
        provider,
        config.authServiceUrl,
        // Without a continuation auth-service lands the member on their home,
        // decided at the OAuth callback once the account is known.
        { invitationToken, returnTo: continuation },
      )
    } catch (err) {
      const fallback = provider === 'google' ? t('login.googleError') : t('login.microsoftError')
      setError(err instanceof Error ? err.message : fallback)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError('')

    // The form is noValidate, so the shape check is here: a name typed into the
    // email field is caught before anything is sent (agion/Agion#2265).
    const signInEmail = email.trim()
    if (!hasEmailShape(signInEmail)) {
      setEmailError(t('authErrors.invalidEmail'))
      return
    }

    // Start async operation without blocking
    const performLogin = async () => {
      try {
        const continuation = returnTo
          ? resolveAuthenticationContinuation(returnTo, config.authServiceUrl)
          : undefined

        // Authenticate once, then resume the authorization transaction that
        // originally sent the browser to Login UI.
        const loginResponse = await fetchRetryingWhenBusy(() => fetch(`${config.apiUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Important: allows receiving session cookie
          body: JSON.stringify({
            email: signInEmail,
            password,
            ...(continuation ? { return_to: continuation } : {}),
          }),
        }))

        if (!loginResponse.ok) {
          throw await AuthRequestError.fromResponse(loginResponse)
        }

        // Parse successful response
        const loginData = await loginResponse.json()

        // The old nested Login UI OIDC callback performed this step. Keep the
        // invitation behavior explicit now that password login resumes the
        // originating transaction directly.
        // An accepted invitation names the organisation it joined and that
        // membership's home (ADR-20260930 D7): the session moves there, then,
        // unless the sign-in link carried its own continuation, the browser
        // lands on that home.
        let acceptedHome: string | null = null
        const invitationToken = sessionStorage.getItem('invitation_token')
        if (invitationToken) {
          try {
            const acceptance = await authService.acceptInvitation(invitationToken)
            acceptedHome = await homeAfterAcceptance(acceptance, (id) => authService.switchOrganization(id))
          } catch (inviteErr) {
            console.warn('Failed to accept pending invitation:', inviteErr)
          } finally {
            sessionStorage.removeItem('invitation_token')
          }
        }

        // Auth Service answers with the validated continuation, or the
        // member's home when there was none. An answer that names neither
        // falls back to the session's home. Validate the owner again before
        // navigation.
        const answered = typeof loginData.redirect_url === 'string' && loginData.redirect_url
          ? (loginData.redirect_url as string)
          : null
        const destination: string | null = (continuation ? answered : acceptedHome ?? answered)
          ?? await readSessionHomeUrl(config.authServiceUrl)
        if (destination) {
          window.location.href = resolveAuthenticationContinuation(destination, config.authServiceUrl)
        } else {
          setError(t('login.missingRedirect'))
        }
      } catch (err) {
        // A waitlisted account whose organization is not active yet: auth-service
        // points to the page that says what happens next and whom to ask.
        if (err instanceof AuthRequestError && err.serverError === ACCOUNT_LOCKED_CODE && err.appRedirect) {
          navigate(err.appRedirect)
          return
        }
        setError(authErrorMessage(err, t, 'login.genericError'))
      }
    }

    // Use transition to mark the UI as loading
    startTransition(() => {
      performLogin()
    })
  }

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('login.formHeading')}</h2>
        <p className="lp-subheading">{t('login.formSubheading')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="lp-form lp-rise lp-rise-3">
        {error && (
          <Alert variant="error">
            {error}
          </Alert>
        )}

        <Field
          id="email"
          type="email"
          label={t('login.emailLabel')}
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          required
          autoComplete="email"
          placeholder={t('login.emailPlaceholder')}
          disabled={isPending}
        />

        <Field
          id="password"
          type={showPassword ? 'text' : 'password'}
          label={t('login.passwordLabel')}
          value={password}
          onChange={handlePasswordChange}
          required
          autoComplete="current-password"
          // eslint-disable-next-line i18next/no-literal-string -- masked-password glyphs, not copy
          placeholder="••••••••"
          disabled={isPending}
          labelAction={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="lp-field-action"
              aria-controls="password"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </Button>
          }
        />

        <Link to="/forgot-password" className="lp-forgot">
          {t('login.forgotPassword')}
        </Link>

        <Button
          type="submit"
          disabled={isPending || !email || !password}
          className="lp-btn-primary w-full"
          isLoading={isPending}
        >
          {isPending ? t('login.signingIn') : t('login.submit')}
          {!isPending && <ArrowRight size={16} aria-hidden="true" />}
        </Button>

        <div className="lp-divider">{t('login.orContinueWith')}</div>

        <div className="lp-social-grid">
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={() => handleExternalLogin('google')}
            disabled={isPending}
            aria-label={t('login.googleSignIn')}
          >
            <GoogleIcon />
            {t('social.google')}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={() => handleExternalLogin('microsoft')}
            disabled={isPending}
            aria-label={t('login.microsoftSignIn')}
          >
            <MicrosoftIcon />
            {t('social.microsoft')}
          </Button>
        </div>
      </form>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        {t('login.newToAgion')}{' '}
        <Link to="/waitlist">{t('login.joinWaitlist')}</Link>
      </p>
    </AuthSplitLayout>
  )
}
